import { useCallback, useState } from 'react';
import { payrollActions } from '@core/payroll/payroll.action';
import type { DateRangeValue } from '@/components/DateRangePicker';
import { downloadTextFile } from '@/modules/payroll/utils/csv.utils';
import { toDateInputValue } from '@/modules/payroll/utils/payrollFormat.utils';
import {
  getLatestCompletedPayRunWeek,
  mapPayRunReadinessCsv,
  type PayRunReadinessRecord,
} from '@/modules/payroll/utils/payRunReadiness.utils';

export type PayRunReadinessState = {
  headers: string[];
  records: PayRunReadinessRecord[];
  isLoading: boolean;
  error: string | null;
  hasRun: boolean;
  dateRange: DateRangeValue;
  setDateRange: (range: DateRangeValue) => void;
  runReport: () => Promise<void>;
  exportCsv: () => void;
};

export const usePayRunReadiness = (
  tenantId: string | null,
): PayRunReadinessState => {
  const [dateRange, setDateRange] = useState<DateRangeValue>(
    getLatestCompletedPayRunWeek,
  );
  const dateFrom = toDateInputValue(dateRange.start);
  const dateTo = toDateInputValue(dateRange.end);
  const [csvText, setCsvText] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [records, setRecords] = useState<PayRunReadinessRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasRun, setHasRun] = useState(false);

  const runReport = useCallback(async () => {
    if (!tenantId) {
      setError('No tenant selected.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const csv = await payrollActions.getPayRunReadiness({
        tenantId,
        startDate: dateFrom,
        endDate: dateTo,
      });
      const report = mapPayRunReadinessCsv(csv);
      setCsvText(csv);
      setHeaders(report.headers);
      setRecords(report.records);
      setHasRun(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Unable to run pay run readiness.',
      );
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, dateFrom, dateTo]);

  const exportCsv = useCallback(() => {
    if (!csvText) {
      return;
    }
    downloadTextFile(`pay-run-readiness-${dateFrom}-to-${dateTo}.csv`, csvText);
  }, [csvText, dateFrom, dateTo]);

  return {
    headers,
    records,
    isLoading,
    error,
    hasRun,
    dateRange,
    setDateRange,
    runReport,
    exportCsv,
  };
};
